"use client";

import { useId, useState } from "react";
import Link from "next/link";
import SoundCard from "@/components/ui/sound-card";
import { Mono } from "@/components/ui/tls/mono";
import { ViewSwitch } from "@/components/ui/tls/view-switch";
import HomeSearchForm from "@/components/sections/HomeSearchForm";
import { downloadCountAriaLabel } from "@/lib/format-count";
import { MORE_TRENDING_HREF } from "@/lib/homepage-rails";
import type { Sound } from "@/lib/supabase";
import { PANEL_CONTAINER_CLASS, PANEL_CONTAINER_STYLE, PANEL_SECTION_RESET_STYLE } from "@/components/sections/shared/panel-grid";

type View = "popular" | "trending";

interface HomeSoundListProps {
  popular: Sound[];
  trending: Sound[];
  soundCountLabel: string;
  /** Catalog-wide install (download) total, shown as a plain caption under the list. */
  totalDownloads?: number | null;
}

const VIEWS: readonly { id: View; label: string }[] = [
  { id: "popular", label: "Popular" },
  { id: "trending", label: "Trending" },
];

const TEXT_LINK_CLASS =
  "tlsr-focus tls-target inline-flex items-center underline decoration-[color:var(--tls-border-strong)] underline-offset-4";

/**
 * The homepage's one playable list. Popular is all-time installs; Trending is
 * the last seven days with Popular's rows removed, so the views never repeat.
 * Both panels stay in the server HTML so every row's link is crawlable; the
 * toggle only hides the inactive one.
 */
export default function HomeSoundList({
  popular,
  trending,
  soundCountLabel,
  totalDownloads = null,
}: HomeSoundListProps) {
  const [view, setView] = useState<View>(popular.length > 0 ? "popular" : "trending");
  const baseId = useId();
  const rows: Record<View, Sound[]> = { popular, trending };
  const toggleable = trending.length > 0 && popular.length > 0;

  if (popular.length === 0 && trending.length === 0) {
    return (
      <section
        id="sounds"
        className="tls-section border-b border-[color:var(--tls-border-subtle)]"
        style={{
          background: "var(--tls-canvas)",
          ...PANEL_SECTION_RESET_STYLE,
        }}
      >
        <div className={PANEL_CONTAINER_CLASS} style={PANEL_CONTAINER_STYLE}>
          <p className="m-0 text-base text-[var(--tls-fg-muted)]">
            The sound list did not load.{" "}
            <Link href="/sounds" className={`${TEXT_LINK_CLASS} font-semibold text-[var(--tls-brand-ink)]`}>
              Open the full library
            </Link>
          </p>
          <div className="mt-6">
            <HomeSearchForm soundCountLabel={soundCountLabel} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="sounds"
      aria-labelledby={`${baseId}-heading`}
      className="tls-section border-b border-[color:var(--tls-border-subtle)]"
      style={{
        background: "var(--tls-canvas)",
        ...PANEL_SECTION_RESET_STYLE,
      }}
    >
      <div className={PANEL_CONTAINER_CLASS} style={PANEL_CONTAINER_STYLE}>
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
          <h2
            id={`${baseId}-heading`}
            className="tls-h2 m-0 text-[var(--tls-fg-strong)]"
          >
            Play one, then download it.
          </h2>
          {toggleable ? <ViewSwitch label="Order" options={VIEWS} value={view} onChange={setView} /> : null}
        </div>

        {VIEWS.map(({ id }) =>
          rows[id].length > 0 ? (
            <div key={id} hidden={view !== id}>
              <div className="mt-6 grid grid-cols-1 gap-0 border-t border-[color:var(--tls-border-subtle)]">
                {rows[id].map((sound) => (
                  <SoundCard
                    key={sound.id}
                    sound={sound}
                    deferAudioUntilVisible
                    disableEntryAnimation
                  />
                ))}
              </div>
              {id === "trending" ? (
                <p className="m-0 mt-4 text-sm text-[var(--tls-fg-muted)]">
                  Most downloaded this week.{" "}
                  <Link href={MORE_TRENDING_HREF} className={TEXT_LINK_CLASS}>
                    More trending
                  </Link>
                </p>
              ) : null}
            </div>
          ) : null,
        )}

        <div className="mt-8">
          <HomeSearchForm soundCountLabel={soundCountLabel} />
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
          <span className="flex flex-wrap items-center gap-x-6">
            <Link href="/sounds" className={`${TEXT_LINK_CLASS} font-semibold text-[var(--tls-brand-ink)]`}>
              All {soundCountLabel} sounds
            </Link>
            {/* The install section sits below browse; on a phone that is a
                long way down, so the list points at it. */}
            <a href="#install" className={`${TEXT_LINK_CLASS} text-sm text-[var(--tls-fg-muted)]`}>
              How to install
            </a>
          </span>
          {totalDownloads ? (
            <Mono size={11} color="var(--tls-fg-subtle)">
              {downloadCountAriaLabel(totalDownloads)} so far
            </Mono>
          ) : null}
        </div>
      </div>
    </section>
  );
}
